import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Helmet } from "react-helmet";
import { useInView } from "react-intersection-observer";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import Button from "../../components/Buttons/Button";
import { useTranslationApp } from "../../lib/i18next";
import { useSelectorApp } from "../../lib/redux";
import useOwnerPromotionalCodes from "../../hooks/useOwnerPromotionalCodes";
import PromotionalCodesTable from "./components/PromotionalCodesTable";
import ModalCreatePromotionalCode from "./components/ModalCreatePromotionalCode";
import { SimpleAlert } from "../../components/Alerts/Alerts";
import {
  ERROR_OCCURRED,
  OWNER_DASHBOARD_NAV_PROMOTIONAL_CODES,
  OWNER_PROMOTIONAL_CODES_CREATE_BUTTON,
} from "../../i18n/keysTranslations";

const OwnerPromotionalCodesBrief = () => {
  const { t } = useTranslationApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const {
    rows,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    onCreateCode,
    onChangeCodeStatus,
  } = useOwnerPromotionalCodes();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [statusUpdatingCodeID, setStatusUpdatingCodeID] = useState(null);

  const { ref: lastItemRef, inView } = useInView({
    triggerOnce: false,
    rootMargin: "80px 0px",
  });

  useEffect(() => {
    if (!inView || !hasMore || loading || loadingMore) return;
    loadMore();
  }, [inView, hasMore, loading, loadingMore, loadMore]);

  const onCallbackSave = async ({
    code,
    maxRedemptions,
    type,
    tier,
    stripeCode,
    discount,
    discountType,
  }) => {
    await onCreateCode({
      code,
      maxRedemptions,
      type,
      tier,
      stripeCode,
      discount,
      discountType,
    });
  };

  const onChangeStatus = async ({ codeID, isActive }) => {
    setStatusUpdatingCodeID(codeID);
    try {
      await onChangeCodeStatus({ codeID, isActive });
    } catch {
      await SimpleAlert({
        title: t(ERROR_OCCURRED),
        icon: "error",
      });
    } finally {
      setStatusUpdatingCodeID(null);
    }
  };

  return (
    <>
      <ContainerPage hasMarginBottom={false}>
        <Helmet>
          <title>
            {`${theme.nameBusiness} - ${t(OWNER_DASHBOARD_NAV_PROMOTIONAL_CODES)}`}
          </title>
        </Helmet>
        <Box sx={{ width: "100%", px: 3, mt: 4, flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <TitlePage sx={{ mb: 0 }}>
              {t(OWNER_DASHBOARD_NAV_PROMOTIONAL_CODES)}
            </TitlePage>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Button
                color="primary"
                variant="contained"
                onClick={() => setIsCreateModalOpen(true)}
              >
                {t(OWNER_PROMOTIONAL_CODES_CREATE_BUTTON)}
              </Button>
            </Box>
          </Box>

          {error ? (
            <Typography color="error" sx={{ mb: 2 }}>
              {t(ERROR_OCCURRED)}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ width: "100%", px: 3, mb: 4, minWidth: 0 }}>
          <PromotionalCodesTable
            rows={rows}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            lastItemRef={lastItemRef}
            onChangeStatus={onChangeStatus}
            statusUpdatingCodeID={statusUpdatingCodeID}
          />
        </Box>
      </ContainerPage>

      <ModalCreatePromotionalCode
        modalOpen={isCreateModalOpen}
        onCloseModal={() => setIsCreateModalOpen(false)}
        onCallbackSave={onCallbackSave}
      />
    </>
  );
};

export default OwnerPromotionalCodesBrief;
